import { Params } from '@angular/router';
import { CmTableActionCell, CmTableDownloadCell } from './table-action-cell.interface';
import { CmTableColumnConfig } from './table-column-config.interface';
import { CmTableColumnType } from './table-column.type';
import { CmTableFilter } from './table-filter.interface';
import { CmTableRow } from './table-row.type';

/**
 * Definition for a table columns
 * @class
 * @property {string} field - The row property tied to this column
 * @property {string} [label] - The display text in the column header
 * @property {string} [localizationKey] - The key used to localize the table header
 * @property {boolean} [tracked = false] - Indicates if the column is used for data tracking
 * @property {object} [width] - The width of the table column
 * @property {number} [width.value] - The value of the column width
 * @property {'px'|'rem'|'%'} [width.unit = 'rem'] - The unit of the column width
 * @property {CmTableColumnType} [type = 'string'] - Determines the type of template used for rendering the cell
 * @property {boolean} [searchable = true] - Determines if the column can be searched on
 * @property {string} [searchField] - An alternative row property to search on
 * @property {sortable} [sortable = true] - Determines if the column can be sorted
 * @property {string} [sortField] - An alternative row property to sort on
 * @property {boolean} [exportable = true] - Determines if the column is included on export
 * @property {string} [exportFIeld] - An alternative row property to export
 * @property {string} [filterField] - An alternative row property to filter on
 * @property {CmTableFilter} [filter] - An object used to build a static filter in teh table meta row
 * @property {boolean} [hidden = false] - When true the column is ignored altogether
 * @property {'_blank' | '_self'} [linkTarget = '_self'] - Target on a link tag
 * @property {boolean} [routerLink = true] - Determines if the link template should use href or routerLink
 * @property {string} [enumType] - If the column type is an enum, this is used as the enumType for lcoalization
 * @property {string} [summaryField] - An alternative row property used for the column summary
 * @property {boolean} isLink - True if the column type is 'link'
 * @property {string} [headerTpl] - ID for the column header template in the table headerTpls object
 * @property {string} [cellTpl] - ID for the column cell template in the table cellTpls object
 * @property {boolean} hideIfEmpty - Hides the column if there is no data in it
 * @property {'start'|'end'|'center'} [align='start'] - Align the cell contents of the column
 * @property {CmTableActionCell[]} [actions] - An array of actions to render in the cell
 */
export class CmTableColumn {
	private _isLink: boolean = false;
	field: string;
	label?: string;
	localizationKey?: string;
	tracked: boolean = false;
	width?: { value: number; unit?: 'px' | 'rem' | '%' | 'em' };
	type: CmTableColumnType = 'string';
	searchable?: boolean = true;
	searchField?: string;
	sortable?: boolean = true;
	sortField?: string;
	exportable?: boolean = true;
	exportField?: string;
	filterField?: string;
	filter?: CmTableFilter;
	hidden?: boolean = false;
	linkTarget: '_blank' | '_self' = '_self';
	linkAriaLabel?: (element: CmTableRow) => string;
	routerLink: boolean = true;
	enumType?: string;
	summaryField?: string;
	actions?: (CmTableActionCell | CmTableDownloadCell)[];
	headerTpl?: string;
	cellTpl?: string;
	hideIfEmpty: boolean = false;
	justify: 'start' | 'center' | 'end' = 'start';
	/**
	 * @method
	 * @param {CmTableRow} element - The row data
	 * @return {string | string[]} - Value used in the href or routerLink. If it returns a falsy value, the field will be displayed like a text field.
	 */
	link?: (element: CmTableRow) => string | string[];
	/**
	 * A resolver to add query params to a link
	 * @method
	 * @param {CmTableRow} element - The row object
	 * @return {Params} - An Angular router query param object
	 */
	queryParams?: (element: CmTableRow) => Params;
	/**
	 * @method
	 * @param {any[]} values - An array of the row values reduced to the property specified by the field
	 * @return {string} - The value to display in the summary
	 */
	summary?: (values: any[]) => string;

	constructor(config: CmTableColumnConfig) {
		this.field = config.field;
		if (Object.hasOwnProperty.call(config, 'label')) {
			this.label = config.label;
		}
		if (Object.hasOwnProperty.call(config, 'localizationKey')) {
			this.localizationKey = config.localizationKey;
		}
		if (Object.hasOwnProperty.call(config, 'tracked')) {
			this.tracked = config.tracked;
		}
		this.type = config.type ? config.type : this.type;
		this.searchField = config.searchField ? config.searchField : config.field;
		this.sortField = config.sortField ? config.sortField : config.field;
		this.exportField = config.exportField ? config.exportField : config.field;
		this.filterField = config.filterField ? config.filterField : config.field;
		this.filter = config.filter ? { id: this.filterField, ...config.filter } : null;

		if (Object.hasOwnProperty.call(config, 'link')) {
			if (!Object.hasOwnProperty.call(config, 'type')) {
				this.type = 'link';
			}
			this.link = config.link;
			this._isLink = true;
			this.linkTarget = config.linkTarget ? config.linkTarget : this.linkTarget;

			if (Object.hasOwnProperty.call(config, 'queryParams')) {
				this.queryParams = config.queryParams;
			}
		}

		if (Object.hasOwnProperty.call(config, 'linkAriaLabel')) {
			this.linkAriaLabel = config.linkAriaLabel;
		}

		if (Object.hasOwnProperty.call(config, 'sortable')) {
			this.sortable = config.sortable;
		}
		if (Object.hasOwnProperty.call(config, 'searchable')) {
			this.searchable = config.searchable;
		}
		if (Object.hasOwnProperty.call(config, 'exportable')) {
			this.exportable = config.exportable;
		}
		if (Object.hasOwnProperty.call(config, 'hidden')) {
			this.hidden = config.hidden;

			// Default searchable/sortable/exportable to false when hidden is set to true and searchable is not
			if (config.hidden) {
				if (!Object.hasOwnProperty.call(config, 'sortable')) {
					this.sortable = false;
				}
				if (!Object.hasOwnProperty.call(config, 'searchable')) {
					this.searchable = false;
				}
				if (!Object.hasOwnProperty.call(config, 'exportable')) {
					this.exportable = false;
				}
			}
		}
		if (Object.hasOwnProperty.call(config, 'routerLink')) {
			this.routerLink = config.routerLink;
		}
		if (Object.hasOwnProperty.call(config, 'summary')) {
			this.summary = config.summary;
			this.summaryField = config.summaryField ? config.summaryField : config.field;
		}
		if (Object.hasOwnProperty.call(config, 'actions')) {
			if (!Object.hasOwnProperty.call(config, 'type')) {
				this.type = 'action';
			}
			this.actions = config.actions;
		}
		if (Object.hasOwnProperty.call(config, 'headerTpl')) {
			this.headerTpl = config.headerTpl;
		}
		if (Object.hasOwnProperty.call(config, 'cellTpl')) {
			this.cellTpl = config.cellTpl;
		}
		if (Object.hasOwnProperty.call(config, 'hideIfEmpty')) {
			this.hideIfEmpty = config.hideIfEmpty;
		}
		if (Object.hasOwnProperty.call(config, 'justify')) {
			this.justify = config.justify;
		}
		if (Object.hasOwnProperty.call(config, 'width')) {
			this.width = config.width;
			if (!this.width.unit) {
				this.width.unit = 'rem';
			}
		}
	}

	get isLink() {
		return this._isLink;
	}
}
